import React, { useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Key, Shield } from 'lucide-react';

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ userid: '', password: '', usertype: 2 });
  const [resetModal, setResetModal] = useState(false);
  const [resetId, setResetId] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchUsers = () => { userAPI.list().then(({ data }) => { setUsers(data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(fetchUsers, []);

  const openCreate = () => { setEditUser(null); setForm({ userid: '', password: '', usertype: 2 }); setShowModal(true); };
  const openEdit = (u) => { setEditUser(u); setForm({ userid: u.userid, password: '', usertype: u.usertype }); setShowModal(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editUser) {
        await userAPI.update(editUser.id, { userid: form.userid, usertype: form.usertype });
        toast.success('User updated');
      } else {
        if (!form.userid || !form.password) { toast.error('User ID and password required'); setSaving(false); return; }
        await userAPI.create(form);
        toast.success('User created');
      }
      setShowModal(false); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.userid?.[0] || err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.userid}"?`)) return;
    try { await userAPI.delete(u.id); toast.success('Deleted'); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleResetPw = async () => {
    if (!newPw) { toast.error('Password required'); return; }
    setSaving(true);
    try { await userAPI.resetPassword(resetId, newPw); toast.success('Password reset'); setResetModal(false); setNewPw(''); }
    catch { toast.error('Failed to reset password'); }
    setSaving(false);
  };

  if (loading) return <div className="page-container"><div className="loading-spinner"><div className="spinner" /></div></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Manage Users</h1><p className="page-subtitle">{users.length} user accounts (UserLogin table)</p></div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> New User</button>
      </div>

      <div className="card"><div className="table-wrapper">
        <table className="data-table">
          <thead><tr><th>User ID</th><th>Type</th><th>Role</th><th style={{ width: 160 }}>Actions</th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ cursor: 'default' }}>
                <td><div className="candidate-cell"><div className="name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {u.is_admin && <Shield size={14} style={{ color: 'var(--accent)' }} />}{u.userid}
                </div></div></td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: '.85rem' }}>{u.usertype}</td>
                <td><span className={`badge ${u.is_admin ? 'badge-admin' : 'badge-recruiter'}`}>{u.role_display}</span></td>
                <td><div className="btn-group">
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)} title="Edit"><Edit size={14} /></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setResetId(u.id); setResetModal(true); }} title="Reset Password"><Key size={14} /></button>
                  {u.userid !== me?.username && <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(u)} title="Delete" style={{ color: 'var(--danger)' }}><Trash2 size={14} /></button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div></div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editUser ? 'Edit User' : 'Create User'}
        footer={<><button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></>}>
        <div className="form-group"><label className="form-label">User ID {!editUser && '*'}</label><input className="form-input" value={form.userid} onChange={e => setForm(p => ({ ...p, userid: e.target.value }))} disabled={!!editUser} /></div>
        {!editUser && <div className="form-group"><label className="form-label">Password *</label><input className="form-input" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} /></div>}
        <div className="form-group"><label className="form-label">User Type</label>
          <select className="form-select" value={form.usertype} onChange={e => setForm(p => ({ ...p, usertype: parseInt(e.target.value) }))}>
            <option value={1}>1 — Admin</option><option value={2}>2 — Recruiter</option>
          </select>
        </div>
      </Modal>

      <Modal isOpen={resetModal} onClose={() => { setResetModal(false); setNewPw(''); }} title="Reset Password"
        footer={<><button className="btn btn-secondary" onClick={() => { setResetModal(false); setNewPw(''); }}>Cancel</button><button className="btn btn-primary" onClick={handleResetPw} disabled={saving}>{saving ? 'Resetting...' : 'Reset'}</button></>}>
        <div className="form-group"><label className="form-label">New Password</label><input className="form-input" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} autoFocus /></div>
      </Modal>
    </div>
  );
}
